// ============================================================
// rateIndexUnits.ts — what the number in a rate-index row MEANS.
//
// MIRROR OF api/src/rateIndexUnits.js — keep the two in lockstep. (api/ does
// not depend on @harvus/shared, so the rule genuinely exists twice.)
//
// dbo.RateIndexes was built for FRED series quoting a rate in PERCENT: 7.5
// means 7.5%, stored as 0.075, rendered back through fmt.pct. The S&P 500
// (`SP500`) is an index LEVEL — 6450.16 points — and down that same path it
// stores as 64.5016 and renders as "645016.00%".
//
// THE DEFAULT IS LOAD-BEARING: an unknown or missing unit is 'percent', never
// 'level'. `Unit` arrives in migration 163 and every row that predates it reads
// as percent, which is exactly what it has always been.
// ============================================================

export const UNIT_PERCENT = "percent";
export const UNIT_LEVEL = "level";

export type RateIndexUnit = typeof UNIT_PERCENT | typeof UNIT_LEVEL;

export const normalizeUnit = (unit?: string | null): RateIndexUnit =>
  String(unit ?? "").trim().toLowerCase() === UNIT_LEVEL ? UNIT_LEVEL : UNIT_PERCENT;

export const isLevelUnit = (unit?: string | null): boolean =>
  normalizeUnit(unit) === UNIT_LEVEL;

// What the user types → what gets stored. A percentage is entered as "5.33" and
// stored as 0.0533; a level is entered and stored as points. Mirrors
// scaleFredValue on the API so a manual override lands on the same value the
// nightly sync would have written. null for anything non-numeric.
export const indexInputToStored = (
  entered: string | number | null | undefined,
  unit?: string | null
): number | null => {
  const n = typeof entered === "number" ? entered : parseFloat(String(entered ?? ""));
  if (!Number.isFinite(n)) return null;
  return isLevelUnit(unit) ? n : n / 100;
};

// The inverse, for seeding the edit box from the stored value.
export const storedToIndexInput = (
  stored: number | null | undefined,
  unit?: string | null
): number => {
  const n = Number(stored ?? 0);
  if (!Number.isFinite(n)) return 0;
  return isLevelUnit(unit) ? n : n * 100;
};

// The label for the edit box, so the user knows which of the two they are typing.
export const indexInputLabel = (unit?: string | null): string =>
  isLevelUnit(unit) ? "New Level" : "New Rate (%)";
